import Phaser from 'phaser';
import { speakRoundIntro } from '@/game/roundIntro';
import i18next from 'i18next';
import { GAME_WIDTH, GAME_HEIGHT, FONT_STACK, WAGON } from '@/game/config';
import { getBackground, getBestWagon, setBestWagon, type Background } from '@/game/storage';
import { fableVariant } from '@/game/theme';
import { Starfield } from '@/game/objects/Starfield';
import { addRoundedLabelBg, pillTexture } from '@/game/ui/roundedLabelBg';
import { addMuteToggle } from '@/game/ui/muteToggle';
import { scriptFontFamily } from '@/game/fonts';
import { survivalPack } from '@/game/packs';
import { ReadingProgression } from '@/game/progression/ReadingProgression';
import { maybeShowBuyPrompt } from '@/game/ui/buyPrompt';
import { maybeAskForRating } from '@/game/rating';
import { trackWagonStart, trackWagonEnd } from '@/analytics';
import type { Pack, PackClass, PackWord } from '@/data/packs/types';
import {
  playPopSound,
  playThudSound,
  playCrackSound,
  playShipSound,
  playRecordSound,
  playRoundCompleteSound,
  speakRulePrompt,
  startBackdropAmbience,
  stopBackdropAmbience,
} from '@/game/audio';
import { buildSchedule, type Drop } from '@/game/wagon/schedule';
import { poolsFor, classFor, eligibleClassIds } from '@/game/wagon/cargo';
import {
  newWagon, resolveLanding, shipWagon, wagonFull, wagonAt, pileFraction, laneAtPointer,
  lanesFor, fallMsFor, spawnMsFor, type WagonState, type WagonWord,
} from '@/game/wagon/track';
import { redirectIfWebLimit } from '@/game/ui/webLimitCard';

interface WagonInit {
  lang: string;
  /** Dev only (`?wagon=5`): start the ladder at the seven-lane board with its forced sacrifices. */
  startRound?: number;
}

/** The fall field: from under the banner down to the wagon's mouth. */
const FIELD_TOP = 132;
/** Where a word arrives — the line the wagon's open top sits on. */
const CATCH_Y = 586;
/** The rail the wagon's wheels sit on — below the catch line, so the wagon rides ON it. */
const RAIL_Y = 656;
/** One crate, and therefore one lane's worth of lost runway (§4.2). */
const CRATE_H = 42;
/** A crate as a share of the fall — what a clogged lane charges against the reading budget. */
const CRATE_FRACTION = CRATE_H / (CATCH_Y - FIELD_TOP);

/** The yard: one spare wagon per life (§5.4). Not the treat glyph (🌌) and not another mode's. */
const SPARE = '🚃';
const PLATE_FILL = 0xe8f4fb;
const PLATE_TEXT = '#1a2430';
const CRATE_FILL = 0xc9a227;
/**
 * The pile's warning colours, by how much room the lane has LEFT rather than by how tall it is.
 *
 * A child cannot count crates against a cap they were never told, so the pile says it in colour:
 * gold while there is room, orange with two left, red when the next miss buries the lane. Keyed on
 * remaining room because the cap itself changes with the rung (WAGON.stackCap is [4, 4, 3, 3, 3]) —
 * colouring by height instead would make the last crate of a 3-cap lane look as safe as the first
 * crate of a 4-cap one, which is the opposite of what the colour is for.
 */
const CRATE_WARN = 0xd8791f;
const CRATE_DANGER = 0xc2392b;
const CRATE_TEXT = '#2a2205';
const WAGON_FILL = 0x2b3a4a;
const WAGON_EDGE = 0x7ec4ff;

/** One word currently falling. */
interface Faller {
  drop: Drop;
  box: Phaser.GameObjects.Container;
  /** Where it stops: the rail, or the top of its lane's pile if it is cargo (§4.2). */
  landY: number;
}

/**
 * Word Wagon — the galaxy reward minigame (specs/wagon-mode.md).
 *
 * Words drop out of a freighter down five to seven lanes; one wagon runs left and right on a rail
 * with a class on its destination sign. Catch the cargo, get out from under everything else.
 *
 * It is the only mode in the game whose answer is a *position* rather than a tap, which is what
 * separates it from Survival: there, declining a word is free and instant, because ignoring is the
 * default. Here the wagon is always standing somewhere, so "that is not a verb" has to be acted on.
 *
 * Self-contained like SurvivalScene, PairsScene and MazeScene: it touches neither
 * RoundScene/BoardScene nor the Leitner engine, and is entered only by spending a banked Galaxy.
 * What falls when is decided by the Phaser-free generator in game/wagon/schedule.ts and what a
 * landing MEANS by game/wagon/track.ts; this scene only draws it.
 */
export class WagonScene extends Phaser.Scene {
  private lang!: string;
  private startRound = 1;

  private words: PackWord[] = [];
  private classes: PackClass[] = [];
  /** Class ids with enough cargo AND enough hazards to carry a wagon (§6.2). */
  private classIds: string[] = [];
  private cls: PackClass | null = null;
  private lastClassId: string | null = null;
  private targets: WagonWord[] = [];
  private hazards: WagonWord[] = [];

  private round = 1;
  private loadsShipped = 0;
  private best = 0;
  private state!: WagonState;

  /** ms since this wagon rolled in — the clock the schedule is written against. */
  private elapsed = 0;
  private drops: Drop[] = [];
  private nextDrop = 0;
  private fallers: Faller[] = [];

  /** Lane geometry for the current rung. */
  private lanes = 5;
  private laneW = GAME_WIDTH / 5;

  /** The wagon's drive: from → to since startedAt, in lane units (track.wagonAt). */
  private fromLane = 2;
  private toLane = 2;
  private driveStartedAt = 0;

  private running = false;
  /** The wagon is rolling off / the next one is rolling in — its x belongs to a tween, not to the
   *  player's finger. */
  private shipping = false;
  private over = false;

  private wagonBox!: Phaser.GameObjects.Container;
  private wagonSign!: Phaser.GameObjects.Text;
  private loadSlots!: Phaser.GameObjects.Graphics;
  private spareIcons: Phaser.GameObjects.Text[] = [];
  private roundText!: Phaser.GameObjects.Text;
  private shippedText!: Phaser.GameObjects.Text;
  private askText!: Phaser.GameObjects.Text;
  private askBg: Phaser.GameObjects.NineSlice | null = null;
  /** Crate sprites per lane, bottom first — the model's own pile is in `state.crates`. */
  private crateBoxes: Phaser.GameObjects.Container[][] = [];

  constructor() {
    super({ key: 'Wagon' });
  }

  init(data: WagonInit): void {
    this.lang = data.lang;
    this.startRound = Math.max(1, Math.floor(data.startRound ?? 1));
    this.round = this.startRound;
    this.loadsShipped = 0;
    this.elapsed = 0;
    this.drops = [];
    this.nextDrop = 0;
    this.fallers = [];
    this.crateBoxes = [];
    this.cls = null;
    this.lastClassId = null;
    this.running = false;
    this.shipping = false;
    this.over = false;
  }

  preload(): void {
    // The player's chosen backdrop, loaded on demand exactly as Pairs and the Ice Maze do it —
    // this mode has no exclusive backdrop either.
    const bg = getBackground();
    if (bg !== 'space') {
      const asset = backgroundAsset(bg);
      if (!this.textures.exists(`bg_${asset}`)) {
        this.load.image(`bg_${asset}`, `/images/background_${asset}.jpg`);
      }
    }
  }

  create(): void {
    if (redirectIfWebLimit(this)) return;   // free web build used up — see webLimitCard.ts
    this.best = getBestWagon();
    trackWagonStart();   // web demo only

    this.drawBackdrop();
    startBackdropAmbience(getBackground());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => stopBackdropAmbience());

    // A hand, and deliberately NOT `body.in-game`: nothing here is aimed at, everything is driven.
    this.input.setDefaultCursor('pointer');

    this.buildPools();
    this.drawTrack();
    this.drawHud();
    this.drawWagon();

    addMuteToggle(this, {
      depth: 50,
      onToggle: (muted) => {
        if (muted) stopBackdropAmbience();
        else startBackdropAmbience(getBackground());
      },
    });

    // One handler for both gestures (§5.1): a press sets the destination, and a press that turns
    // into a drag keeps setting it. Neither ever PLACES the wagon — without the drive there is no
    // decision window, and the mode collapses into Survival with extra steps.
    // Tap to send the wagon, hold and slide to keep steering. Both steer on a FRACTIONAL lane
    // (wagon/track.ts laneAtPointer): rounding here is what made a drag move in lane-sized hops
    // instead of following the hand.
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => this.driveTo(this.laneUnderX(p.worldX)));
    this.input.on('pointerup', () => {
      // Snap on release, and for fairness rather than tidiness: `moveFallers` rounds the wagon's
      // lane when it decides what was caught, so a wagon parked on a boundary collects in a lane it
      // is only half standing in — the kind of unfairness a child feels and cannot name.
      this.driveTo(Math.round(this.toLane));
    });
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (p.isDown) this.driveTo(this.laneUnderX(p.worldX));
    });
    this.input.keyboard?.on('keydown-LEFT', () => this.driveTo(Math.round(this.wagonLane()) - 1));
    this.input.keyboard?.on('keydown-RIGHT', () => this.driveTo(Math.round(this.wagonLane()) + 1));

    this.state = newWagon(this.round, WAGON.startWagons);
    this.showHowTo(() => this.startWagon(this.round));
  }

  // ── content ──────────────────────────────────────────────────────────────────

  /**
   * The cargo: the ACTIVE lesson's own pool, through the selection SurvivalScene already performs.
   *
   * That is what makes this mode free in all ten languages and in every lesson (§6.1) — a Galaxy
   * earned in the Fables journey opens a fables wagon, one earned in Der-Die-Das opens a wagon
   * whose sign says "die". `survivalPack()` also handles the lessons that cannot produce a sign at
   * all: a gap-mode or sentence-order lesson has no class to write on one, and it swaps them for a
   * class-match lesson rather than this mode repeating the fix.
   */
  private buildPools(): void {
    const pool: Pack = survivalPack();
    // Read-only use: beginSession() is deliberately NOT called — a reward must not write to the
    // journey. Learned words first, so the wagon is never where a child meets a word for the first
    // time; that matters more here than in Survival, because an unknown word may be a hazard.
    this.words = new ReadingProgression(this.lang, pool).survivalWords();
    this.classes = pool.classes ?? [];

    this.classIds = eligibleClassIds(this.words, pool);
  }

  /**
   * The class on the sign. Wagon 1 is always verbs where the pool has them — it is what the mode
   * was asked for and the class with the clearest edge — and every wagon after that changes it,
   * never repeating the one before (§5.6). The sign is the drill: a class that stayed put for a
   * whole run would be learnable by shape after two wagons.
   */
  private chooseClass(round: number): void {
    let id: string;
    if (round <= 1 && this.classIds.includes('verb')) {
      id = 'verb';
    } else {
      const others = this.classIds.filter((c) => c !== this.lastClassId);
      const from = others.length > 0 ? others : this.classIds;
      id = from[Math.floor(Math.random() * from.length)];
    }
    this.lastClassId = id;
    // As in Survival: a synthesized class has the bare id for a label, which is also its whole
    // prompt — good enough for a sign, and it only happens for a pack that declares no classes.
    this.cls = classFor(this.classes, id);
    const pools = poolsFor(this.words, this.classes, id);
    this.targets = pools.targets;
    this.hazards = pools.hazards;
  }

  // ── the run ──────────────────────────────────────────────────────────────────

  private startWagon(round: number): void {
    this.round = round;
    this.lanes = lanesFor(round);
    this.laneW = GAME_WIDTH / this.lanes;
    this.state = newWagon(round, this.state.wagons);
    this.elapsed = 0;
    this.nextDrop = 0;
    this.chooseClass(round);

    this.fromLane = this.toLane = Math.min(Math.floor(this.lanes / 2), this.lanes - 1);
    this.driveStartedAt = 0;

    this.clearCrates();
    this.roundText.setText(i18next.t('wagon.round', { n: round }));
    this.paintLoad();
    this.paintSpares();
    this.positionWagon();
    this.setSign(this.cls);

    this.drops = buildSchedule({
      round,
      targets: this.targets,
      hazards: this.hazards,
      startLane: this.fromLane,
    });

    // The sign is held — and spoken — before anything falls. The class may never change under a
    // word already in the air (§5.6): a correct read turning wrong in flight is the one thing a
    // game that costs a life per mistake must never do.
    this.running = false;
    this.time.delayedCall(WAGON.signMs, () => {
      if (this.over) return;
      this.running = true;
    });
    // Capped at the opening rounds like every other mode (game/roundIntro.ts). It used to speak
    // on every single wagon, which by the fifth is a voice talking over the game.
    speakRoundIntro(this.cls?.prompt ?? '', this.lang, round);
  }

  update(_time: number, delta: number): void {
    if (this.over) return;
    this.positionWagon();
    if (!this.running) return;

    this.elapsed += delta;
    this.spawnDue();
    this.moveFallers();
  }

  /** Release everything the schedule says is due, and top the batch up before it runs dry. */
  private spawnDue(): void {
    while (this.nextDrop < this.drops.length && this.drops[this.nextDrop].spawnAt <= this.elapsed) {
      this.addFaller(this.drops[this.nextDrop]);
      this.nextDrop++;
    }
    if (this.nextDrop < this.drops.length) return;

    // The batch is out and the wagon is not full: schedule another, starting from where the wagon
    // actually is, so the walkability guarantee holds across the seam as well as inside a batch.
    const offset = this.elapsed + spawnMsFor(this.round);
    const more = buildSchedule({
      round: this.round,
      targets: this.targets,
      hazards: this.hazards,
      startLane: Math.round(this.wagonLane()),
    });
    this.drops = more.map((d) => ({ ...d, spawnAt: d.spawnAt + offset, landAt: d.landAt + offset }));
    this.nextDrop = 0;
  }

  private addFaller(drop: Drop): void {
    const w = Math.min(this.laneW - 18, 240);
    const h = 62;
    const box = this.add.container(this.laneX(drop.lane), FIELD_TOP).setDepth(20);
    const plate = this.add
      .nineslice(0, 0, pillTexture(this, 14, PLATE_FILL, 0.96), undefined, w, h, 14, 14, 14, 14)
      .setOrigin(0.5);
    const label = this.add.text(0, 0, drop.word.text, {
      fontFamily: scriptFontFamily(this.lang), fontSize: '32px', color: PLATE_TEXT,
    }).setOrigin(0.5);
    // Auto-shrink to the lane, with a floor: a seven-lane board is 183px wide per lane and some
    // words are 14 characters long.
    const maxW = w - 22;
    if (label.width > maxW) {
      const scaled = Math.max(WAGON.minFontPx, Math.floor(32 * (maxW / label.width)));
      label.setFontSize(scaled);
    }
    box.add([plate, label]);

    // Cargo stops on top of its lane's pile; a hazard falls the whole way whatever is in the lane
    // and drops through the grate (§5.2 / track.pileFraction).
    const landY = drop.word.target
      ? FIELD_TOP + (CATCH_Y - FIELD_TOP) * (1 - pileFraction(this.state, drop.lane, CRATE_FRACTION))
      : CATCH_Y;
    this.fallers.push({ drop, box, landY });
  }

  private moveFallers(): void {
    const speed = (CATCH_Y - FIELD_TOP) / fallMsFor(this.round);
    const wagonLane = Math.round(this.wagonLane());
    const landed: Faller[] = [];

    for (const f of this.fallers) {
      f.box.y = FIELD_TOP + (this.elapsed - f.drop.spawnAt) * speed;
      // Re-read the pile every frame: a crate that lands under a falling word shortens ITS runway
      // too, which is the pressure of §4.2 and has to be honest about arriving sooner.
      if (f.drop.word.target) {
        f.landY = FIELD_TOP + (CATCH_Y - FIELD_TOP) * (1 - pileFraction(this.state, f.drop.lane, CRATE_FRACTION));
      }
      if (f.box.y >= f.landY) landed.push(f);
    }

    for (const f of landed) {
      // A landing can end the wagon (full) or the run (last battery); everything still arriving in
      // the same frame is then swept rather than judged under a board that no longer exists.
      if (!this.running || this.over) { f.box.destroy(); continue; }
      this.land(f, wagonLane);
    }
    if (landed.length > 0) this.fallers = this.fallers.filter((f) => !landed.includes(f));
  }

  /** One word arriving. The model decides what it means; this only plays it. */
  private land(f: Faller, wagonLane: number): void {
    const { state, landing } = resolveLanding(this.state, f.drop.word, f.drop.lane, wagonLane);
    const wagonLost = state.wagons < this.state.wagons;
    let overflowed = false;
    this.state = state;

    switch (landing.kind) {
      case 'caught':
        playPopSound();
        this.scoopInto(f);
        this.paintLoad();
        if (wagonFull(this.state)) this.ship();
        return;
      case 'burst':
        playThudSound();
        this.burst(f);
        break;
      case 'crate':
        this.addCrate(f);
        break;
      case 'overflow':
        playCrackSound();
        this.overflowLane(f.drop.lane);
        f.box.destroy();
        // A buried lane ends the ROUND as well as costing a wagon (§5.4). It used to sweep the lane
        // and play on, which made the most expensive thing in the mode the least visible: a child
        // lost a 🚃 and the board looked much as it had a second earlier. Ending the round is what
        // makes the cost legible, and re-dealing the SAME rung rather than advancing is the rule
        // the Ice Maze and the Labyrinth already use — a rung you failed has not been earned.
        overflowed = true;
        break;
      case 'through':
        this.fallThrough(f);
        break;
    }

    if (wagonLost) {
      this.paintSpares();
      if (this.state.wagons <= 0) { this.gameOver(); return; }
    }
    if (overflowed) this.endRoundBuried();
  }

  /**
   * A lane overflowed: the round is over. The wagon already paid for it, so this only has to clear
   * the board and re-deal the same rung.
   *
   * `startWagon` carries `state.wagons` across, so the life lost above is not lost twice; and the
   * air is swept rather than re-judged, exactly as `ship()` sweeps it, because everything still
   * falling was released against a board that no longer exists.
   */
  private endRoundBuried(): void {
    if (this.over) return;
    this.running = false;
    for (const f of this.fallers) f.box.destroy();
    this.fallers = [];
    this.floatText(GAME_WIDTH / 2, CATCH_Y - 120, i18next.t('wagon.buried'), '#ff8f8f');
    this.time.delayedCall(1600, () => {
      if (this.over) return;
      this.startWagon(this.round);
    });
  }

  // ── the wagon ────────────────────────────────────────────────────────────────

  private laneX(lane: number): number {
    return this.laneW * (lane + 0.5);
  }

  private laneUnderX(x: number): number {
    return laneAtPointer(x, this.laneW, this.lanes);
  }

  /** Where the wagon is right now, in lane units (fractional while it drives). */
  private wagonLane(): number {
    return wagonAt(this.fromLane, this.toLane, this.driveStartedAt, this.time.now);
  }

  private driveTo(lane: number): void {
    if (this.over) return;
    const to = Phaser.Math.Clamp(lane, 0, this.lanes - 1);
    if (to === this.toLane) return;
    this.fromLane = this.wagonLane();
    this.toLane = to;
    this.driveStartedAt = this.time.now;
  }

  private positionWagon(): void {
    // While a wagon is leaving or arriving its x belongs to the tween; steering it from the lane
    // model at the same time would pin it to the rail and it would never get off the screen.
    if (this.shipping) return;
    this.wagonBox.x = this.laneX(this.wagonLane());
  }

  /**
   * The wagon: a mine cart, drawn rather than assembled out of rounded rectangles.
   *
   * It is the one object on screen the whole mode is named after and the only thing the child
   * owns, so it earns real drawing — a body that flares toward its mouth (which is also what makes
   * the opening read as something words fall INTO), a riveted lip, panel seams, spoked wheels on
   * an axle, and a shadow on the rail under it. The gold sign plate on its side is the same gold
   * as the crates on the track: what it is collecting, in the colour of what it collects.
   */
  private drawWagon(): void {
    const box = this.add.container(this.laneX(this.fromLane), CATCH_Y + 24).setDepth(30);
    const W = 168;
    const H = 66;
    const topX = W / 2;
    const botX = W / 2 * 0.82;          // the flare: a cart is wider at the mouth than at the floor
    const topY = -H / 2;
    const botY = H / 2;

    const g = this.add.graphics();

    // The shadow it casts on the rail — soft, and close, because it is standing on the thing.
    g.fillStyle(0x000000, 0.38).fillEllipse(0, botY + 30, W * 0.86, 11);

    // Body, then a lit band across the top third and a dark one along the floor: two flat washes
    // do the work of a gradient, which Phaser's Graphics has none of.
    g.fillStyle(WAGON_FILL, 0.98);
    g.beginPath();
    g.moveTo(-topX, topY);
    g.lineTo(topX, topY);
    g.lineTo(botX, botY);
    g.lineTo(-botX, botY);
    g.closePath();
    g.fillPath();

    g.fillStyle(0x3d5166, 0.85);
    g.beginPath();
    g.moveTo(-topX + 3, topY + 4);
    g.lineTo(topX - 3, topY + 4);
    g.lineTo(topX - 8, topY + H * 0.34);
    g.lineTo(-topX + 8, topY + H * 0.34);
    g.closePath();
    g.fillPath();

    g.fillStyle(0x141c25, 0.55);
    g.beginPath();
    g.moveTo(-botX - 4, botY - 12);
    g.lineTo(botX + 4, botY - 12);
    g.lineTo(botX, botY);
    g.lineTo(-botX, botY);
    g.closePath();
    g.fillPath();

    // Panel seams — two of them, so the body reads as riveted plate rather than as one slab.
    g.lineStyle(2, 0x141c25, 0.7);
    for (const k of [-0.42, 0.42]) {
      g.beginPath();
      g.moveTo(topX * k, topY + 6);
      g.lineTo(botX * k, botY - 2);
      g.strokePath();
    }

    // The lip: a bright overhanging rim, with rivets punched along it.
    g.fillStyle(WAGON_EDGE, 0.95).fillRoundedRect(-topX - 6, topY - 8, W + 12, 9, 4);
    g.fillStyle(0x0d141b, 0.55).fillRect(-topX + 2, topY + 1, W - 4, 3);   // the shaded inner edge
    g.fillStyle(0xdff0ff, 0.85);
    for (const k of [-0.86, -0.52, -0.18, 0.18, 0.52, 0.86]) g.fillCircle(topX * k, topY - 3.5, 2.2);

    // Wheels on an axle. Spokes cost four lines each and are the difference between a wheel and a
    // black dot at this size.
    const wy = botY + 16;
    g.fillStyle(0x141c25, 0.95).fillRect(-W * 0.28, wy - 3, W * 0.56, 6);
    for (const wx of [-W * 0.28, W * 0.28]) {
      g.fillStyle(0x0d141b, 1).fillCircle(wx, wy, 15);
      g.lineStyle(3, WAGON_EDGE, 0.95).strokeCircle(wx, wy, 15);
      // Three lines, not four: six spokes read as a wheel at r=15, eight read as an asterisk.
      g.lineStyle(2, WAGON_EDGE, 0.45);
      for (let i = 0; i < 3; i++) {
        const a = (Math.PI / 3) * i;
        g.beginPath();
        g.moveTo(wx - Math.cos(a) * 12, -Math.sin(a) * 12 + wy);
        g.lineTo(wx + Math.cos(a) * 12, Math.sin(a) * 12 + wy);
        g.strokePath();
      }
      g.fillStyle(WAGON_EDGE, 0.95).fillCircle(wx, wy, 4);
    }

    // The destination plate, in the crates' own gold.
    const plate = this.add.graphics();
    plate.fillStyle(0x141c25, 0.55).fillRoundedRect(-63, -23, 126, 26, 7);
    plate.fillStyle(CRATE_FILL, 0.96).fillRoundedRect(-61, -25, 122, 26, 7);
    plate.lineStyle(2, 0xf0d98a, 0.9).strokeRoundedRect(-61, -25, 122, 26, 7);

    this.wagonSign = this.add.text(0, -12, '', {
      fontFamily: scriptFontFamily(this.lang), fontSize: '19px', color: CRATE_TEXT,
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // The load, drawn on the body as filled slots rather than a number: a five-year-old reads four
    // full boxes faster than they read "4/8". Drawn, not written — ▮/▯ are not in every font this
    // game ships to, and a load counter that renders as tofu on one device is a bad way to find out.
    this.loadSlots = this.add.graphics();

    box.add([g, plate, this.wagonSign, this.loadSlots]);
    this.wagonBox = box;
  }

  private setSign(cls: PackClass | null): void {
    const label = cls?.label ?? '';

    // The plate is 122px wide and a class label can be "Weather & Sky" — measure, then step down
    // until it fits rather than guessing at one size that suits every language's longest topic.
    this.wagonSign.setFontSize(19);
    this.wagonSign.setText(label);
    for (let px = 19; px > 11 && this.wagonSign.width > 112; px--) this.wagonSign.setFontSize(px);

    // The banner asks in words rather than labelling: "Lade Wörter zum Thema: Tiere" tells a child
    // what to DO, where the bare "Fracht: Tiere" only named the freight. It is longer, so it gets
    // the same measure-and-step-down treatment — the pill must not run off either edge.
    this.askText.setFontSize(34);
    this.askText.setText(i18next.t('wagon.sign', { class: label }));
    for (let px = 34; px > 20 && this.askText.width > 1120; px--) this.askText.setFontSize(px);
    this.askBg?.destroy();
    this.askBg = addRoundedLabelBg(this, this.askText);
    this.askText.setInteractive({ useHandCursor: true });
  }

  private paintLoad(): void {
    const n = this.state.capacity;
    const gap = 4;
    const w = Math.min(15, Math.floor((118 - (n - 1) * gap) / n));
    const total = n * w + (n - 1) * gap;
    const y = 10;
    this.loadSlots.clear();
    for (let i = 0; i < n; i++) {
      const x = -total / 2 + i * (w + gap);
      if (i < this.state.load) {
        // A filled slot is a little crate, in the same gold as the ones on the track: what the
        // wagon is carrying is exactly what it failed to carry when it lands out there.
        this.loadSlots.fillStyle(CRATE_FILL, 0.95).fillRect(x, y, w, 12);
        this.loadSlots.fillStyle(0xf0d98a, 0.8).fillRect(x, y, w, 3);
      } else {
        this.loadSlots.lineStyle(2, 0x8fb6d8, 0.45).strokeRect(x + 1, y + 1, w - 2, 10);
      }
    }
  }

  /** A caught word slides into the wagon rather than vanishing where it landed. */
  private scoopInto(f: Faller): void {
    this.tweens.add({
      targets: f.box,
      x: this.wagonBox.x,
      y: CATCH_Y + 20,
      scale: 0.3,
      alpha: 0,
      duration: 220,
      ease: 'Quad.easeIn',
      onComplete: () => f.box.destroy(),
    });
  }

  /** A hazard caught: it bursts, and the wagon jolts so the cost is felt where it happened. */
  private burst(f: Faller): void {
    this.tweens.add({
      targets: f.box, scale: 1.35, alpha: 0, angle: 12, duration: 260, ease: 'Quad.easeOut',
      onComplete: () => f.box.destroy(),
    });
    this.tweens.add({
      targets: this.wagonBox, angle: { from: -6, to: 0 }, duration: 320, ease: 'Bounce.easeOut',
    });
    this.cameras.main.shake(140, 0.006);
  }

  /** A hazard nobody was under: through the grate, gone, free. */
  private fallThrough(f: Faller): void {
    this.tweens.add({
      targets: f.box, y: RAIL_Y + 40, alpha: 0, duration: 260, ease: 'Quad.easeIn',
      onComplete: () => f.box.destroy(),
    });
  }

  // ── the pile ─────────────────────────────────────────────────────────────────

  private addCrate(f: Faller): void {
    const lane = f.drop.lane;
    const height = this.state.crates[lane].length;   // the model already counted this one
    const y = CATCH_Y - CRATE_H * (height - 0.5);
    f.box.destroy();

    // Room left in this lane once this crate has landed: 1 means the next miss buries it.
    const room = this.state.stackCap - height;
    const fill = room <= 1 ? CRATE_DANGER : room === 2 ? CRATE_WARN : CRATE_FILL;

    const box = this.add.container(this.laneX(lane), y - 30).setDepth(15).setAlpha(0);
    const w = Math.min(this.laneW - 26, 220);
    const plate = this.add
      .nineslice(0, 0, pillTexture(this, 8, fill, 0.92), undefined, w, CRATE_H - 6, 8, 8, 8, 8)
      .setOrigin(0.5);
    const label = this.add.text(0, 0, f.drop.word.text, {
      fontFamily: scriptFontFamily(this.lang),
      fontSize: '20px',
      // The warning fills are dark enough that the crate's near-black lettering stops being a word.
      color: fill === CRATE_FILL ? CRATE_TEXT : '#fff4e2',
    }).setOrigin(0.5);
    if (label.width > w - 16) label.setFontSize(Math.max(13, Math.floor(20 * ((w - 16) / label.width))));
    box.add([plate, label]);
    this.crateBoxes[lane] = this.crateBoxes[lane] ?? [];
    this.crateBoxes[lane].push(box);

    playThudSound();
    this.tweens.add({ targets: box, y, alpha: 1, duration: 180, ease: 'Quad.easeIn' });
  }

  /** A lane that took one crate too many: it goes up, and a battery goes with it (§5.4). */
  private overflowLane(lane: number): void {
    const boxes = this.crateBoxes[lane] ?? [];
    this.crateBoxes[lane] = [];

    // The pile BURNS DOWN, from the crate that broke the lane to the one on the rail. Crates used to
    // drift upward and fade, which read as the lane being tidied away — the opposite of what had
    // just happened. A fire eating its way down the stack is the same event told honestly, and it
    // is the one moment in the mode worth spending animation on: it costs a 🚃 and ends the round.
    const stack = [...boxes].sort((a, b) => a.y - b.y);   // topmost first
    stack.forEach((b, i) => {
      const delay = i * 120;
      this.flameOver(b.x, b.y, delay);
      // Catches: a short flare-up before it goes.
      this.tweens.add({
        targets: b, delay, duration: 100, scaleX: 1.1, scaleY: 0.86, ease: 'Sine.easeOut',
      });
      // Collapses: down onto what is left of the pile rather than up off the board, flattened to
      // embers. Charring is left to the flame — a Container cannot be tinted.
      this.tweens.add({
        targets: b, delay: delay + 100, duration: 420,
        y: b.y + CRATE_H * 0.45, scaleX: 0.25, scaleY: 0.08, alpha: 0,
        angle: (Math.random() - 0.5) * 24, ease: 'Quad.easeIn',
        onComplete: () => b.destroy(),
      });
    });

    this.floatText(this.laneX(lane), CATCH_Y - 80, i18next.t('wagon.overflow'), '#ff8f8f');
  }

  /**
   * Three tongues of fire over one crate, staggered so the flame flickers rather than pulses.
   *
   * Drawn as Graphics and tweened, like every other effect in this game — there is no particle
   * system anywhere in the codebase and this is not the place to introduce one. Each blade rises,
   * narrows and stretches as it fades, which is what reads as fire without a single frame of art.
   */
  private flameOver(x: number, y: number, delay: number): void {
    for (let i = 0; i < 3; i++) {
      const w = 14 + Math.random() * 8;
      const h = 30 + Math.random() * 16;
      const g = this.add.graphics().setDepth(18).setAlpha(0);
      g.fillStyle(0xff7b1f, 0.9).fillEllipse(0, 0, w, h);
      g.fillStyle(0xffd166, 0.95).fillEllipse(0, h * 0.14, w * 0.5, h * 0.55);
      g.setPosition(x + (Math.random() - 0.5) * 26, y);
      this.tweens.add({
        targets: g, delay: delay + i * 70, duration: 120, alpha: 1, ease: 'Quad.easeOut',
      });
      this.tweens.add({
        targets: g, delay: delay + i * 70 + 120, duration: 380 + Math.random() * 160,
        y: g.y - 46 - Math.random() * 22, alpha: 0, scaleX: 0.4, scaleY: 1.35,
        ease: 'Quad.easeIn', onComplete: () => g.destroy(),
      });
    }
  }

  private clearCrates(): void {
    for (const lane of this.crateBoxes) for (const b of lane ?? []) b.destroy();
    this.crateBoxes = Array.from({ length: this.lanes }, () => []);
  }

  // ── shipping ─────────────────────────────────────────────────────────────────

  /**
   * The wagon is full: it rolls off to the right and takes every crate with it, which is the only
   * thing that clears the board (§5.5). Read well and you are handed a clean board every few
   * seconds; read badly and you watch the runway shorten.
   */
  private ship(): void {
    this.running = false;
    this.shipping = true;
    this.loadsShipped++;
    this.shippedText.setText(i18next.t('wagon.shipped', { n: this.loadsShipped }));
    playShipSound();

    const perfect = this.state.perfect;

    // The air clears with the crates: everything still falling was released under the OLD sign, so
    // it is swept rather than re-judged (§5.6). It costs nothing either way.
    for (const f of this.fallers) {
      this.tweens.add({
        targets: f.box, x: GAME_WIDTH + 200, alpha: 0, duration: 500, ease: 'Quad.easeIn',
        onComplete: () => f.box.destroy(),
      });
    }
    this.fallers = [];

    for (const lane of this.crateBoxes) {
      for (const b of lane ?? []) {
        this.tweens.add({
          targets: b, x: GAME_WIDTH + 200, duration: 600, delay: Math.random() * 120,
          ease: 'Quad.easeIn', onComplete: () => b.destroy(),
        });
      }
    }
    this.crateBoxes = Array.from({ length: this.lanes }, () => []);

    const next = this.round + 1;
    const shipped = shipWagon(this.state, next);
    if (perfect) this.floatText(this.wagonBox.x, CATCH_Y - 60, i18next.t('wagon.wagonBack'), '#4ecdc4');

    this.tweens.add({
      targets: this.wagonBox, x: GAME_WIDTH + 220, duration: 700, ease: 'Quad.easeIn',
      onComplete: () => {
        if (this.over) return;
        this.state = shipped;
        // The next rung is set up first — lanes, sign, schedule — and only then does the empty
        // wagon roll in from the left, so it arrives already carrying the new sign. It is in place
        // long before the freighter starts: the arrival is 500 ms and the sign is held for signMs.
        this.startWagon(next);
        this.wagonBox.x = -220;
        this.tweens.add({
          targets: this.wagonBox, x: this.laneX(this.fromLane), duration: 500, ease: 'Quad.easeOut',
          onComplete: () => { this.shipping = false; },
        });
      },
    });
  }

  // ── HUD ──────────────────────────────────────────────────────────────────────

  private drawBackdrop(): void {
    const bg = getBackground();
    if (bg === 'space') { new Starfield(this); return; }
    const key = `bg_${backgroundAsset(bg)}`;
    if (this.textures.exists(key)) {
      const img = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, key);
      img.setScale(Math.max(GAME_WIDTH / img.width, GAME_HEIGHT / img.height));
      img.setDepth(-1);
    } else {
      new Starfield(this);
    }
  }

  /** The freighter overhead and the rail the wagon runs on. */
  private drawTrack(): void {
    // Inset to x 220-1060 and held under the banner line: the spare wagons run to x 206 on the
    // left and the run's readouts to x 1050 on the right, and a hull behind either of them reads
    // as clutter rather than as a ship.
    const hull = this.add.graphics().setDepth(5);
    hull.fillStyle(0x2b3a4a, 0.92).fillRoundedRect(220, 92, GAME_WIDTH - 440, 28, 12);
    hull.fillStyle(0x7ec4ff, 0.32).fillRoundedRect(250, 118, GAME_WIDTH - 500, 6, 3);

    const rail = this.add.graphics().setDepth(10);
    rail.fillStyle(0x7ec4ff, 0.5).fillRect(0, RAIL_Y, GAME_WIDTH, 5);
    rail.fillStyle(0x1a2430, 0.75).fillRect(0, RAIL_Y + 5, GAME_WIDTH, 26);
  }

  private drawHud(): void {
    // The round mode's HUD, corner for corner, as Pairs and the Ice Maze lay it out: back pill
    // top-left with the lives directly under it, the run's readouts top-right, mute bottom-left.
    const back = this.drawBackButton();

    const y = back.y + back.height + 10;
    this.spareIcons = [];
    for (let i = 0; i < WAGON.startWagons; i++) {
      this.spareIcons.push(this.add.text(back.x + i * 40, y, SPARE, {
        fontFamily: FONT_STACK, fontSize: '30px',
      }).setDepth(40));
    }

    this.roundText = this.add.text(GAME_WIDTH - 24, 20, '', {
      fontFamily: FONT_STACK, fontSize: '28px', color: '#ffd166',
    }).setOrigin(1, 0).setDepth(40);

    this.shippedText = this.add.text(GAME_WIDTH - 24, 58, i18next.t('wagon.shipped', { n: 0 }), {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#ffffff',
    }).setOrigin(1, 0).setDepth(40);

    // The sign, repeated where every lesson puts its question: a gold pill across the top, same
    // line, size and colour as BoardScene's and Survival's rule banner. Tapping it re-speaks it.
    this.askText = this.add.text(GAME_WIDTH / 2, 56, '', {
      fontFamily: FONT_STACK, fontSize: '34px', color: '#ffd166', align: 'center',
      padding: { x: 18, y: 10 },
    }).setOrigin(0.5).setDepth(50).setInteractive({ useHandCursor: true });
    this.askText.on('pointerdown', () => speakRulePrompt(this.cls?.prompt ?? '', this.lang));
  }

  /** A wrecked wagon is the same glyph dimmed — the trick Pairs uses for a spent brain. */
  private paintSpares(): void {
    this.spareIcons.forEach((b, i) => b.setAlpha(i < this.state.wagons ? 1 : 0.3));
  }

  private floatText(x: number, y: number, text: string, color: string): void {
    const label = this.add.text(x, y, text, {
      fontFamily: FONT_STACK, fontSize: '30px', color,
    }).setOrigin(0.5).setDepth(90);
    this.tweens.add({
      targets: label, y: y - 60, alpha: 0, duration: 1100, ease: 'Sine.easeOut',
      onComplete: () => label.destroy(),
    });
  }

  private drawBackButton(): Phaser.GameObjects.Text {
    const back = this.add.text(12, 16, `← ${i18next.t('nav.menu')}`, {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#dfe6ee', padding: { x: 20, y: 12 },
    }).setOrigin(0, 0).setDepth(50);
    back.setInteractive({
      hitArea: new Phaser.Geom.Rectangle(
        -back.x, -back.y, back.width + back.x + 10, back.height + back.y + 10,
      ),
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: true,
    });
    addRoundedLabelBg(this, back);
    back.on('pointerover', () => back.setColor('#ffffff'));
    back.on('pointerout', () => back.setColor('#dfe6ee'));
    // Straight out, no confirm — the decision Pairs settled. Leaving forfeits the Galaxy; it was
    // spent on entry.
    back.on('pointerdown', () => this.scene.start('Menu'));
    return back;
  }

  // ── overlays ─────────────────────────────────────────────────────────────────

  private showHowTo(onStart: () => void): void {
    // The lesson's help panel, plate for plate — the same card Pairs and the Ice Maze open on, and
    // re-shown every run rather than once ever: a treat run is rare enough that a child may not
    // have played one in weeks.
    const layer = this.add.container(0, 0).setDepth(200);
    const shade = this.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.85,
    );
    shade.setInteractive();
    const panel = this.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH * 0.85, GAME_HEIGHT * 0.78, 0x1a1f25,
    ).setStrokeStyle(4, 0xffd166);
    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 210, i18next.t('wagon.howToTitle'), {
      fontFamily: FONT_STACK, fontSize: '64px', color: '#ffd166', fontStyle: 'bold',
    }).setOrigin(0.5);
    const body = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20, i18next.t('wagon.howToBody'), {
      fontFamily: FONT_STACK, fontSize: '28px', color: '#ffffff', align: 'center',
      wordWrap: { width: GAME_WIDTH * 0.72 }, lineSpacing: 8,
    }).setOrigin(0.5);
    const yard = this.add.text(
      GAME_WIDTH / 2, GAME_HEIGHT / 2 + 100, SPARE.repeat(WAGON.startWagons),
      { fontFamily: FONT_STACK, fontSize: '38px' },
    ).setOrigin(0.5);
    const start = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 200, i18next.t('wagon.howToStart'), {
      fontFamily: FONT_STACK, fontSize: '36px', color: '#ffffff',
      backgroundColor: '#7ec4ff', padding: { x: 40, y: 14 }, fontStyle: 'bold',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    layer.add([shade, panel, title, body, yard, start]);

    // Tap anywhere to start, behind a TIMESTAMP guard (not a delayed registration) so the tap that
    // spent the Galaxy can't skip the card it just opened, and a stalled scene clock — a
    // backgrounded tab — can never strand the child on the card.
    const openedAt = performance.now();
    const dismiss = (): void => {
      if (performance.now() - openedAt < 350) return;
      this.input.off('pointerdown', dismiss);
      layer.destroy(true);
      onStart();
    };
    this.input.on('pointerdown', dismiss);
  }

  // ── end of run ───────────────────────────────────────────────────────────────

  private gameOver(): void {
    if (this.over) return;
    this.over = true;
    this.running = false;

    for (const f of this.fallers) f.box.destroy();
    this.fallers = [];

    trackWagonEnd(this.loadsShipped);   // web demo only

    const isRecord = this.loadsShipped > this.best;
    if (isRecord) { setBestWagon(this.loadsShipped); this.best = this.loadsShipped; playRecordSound(); }
    else playRoundCompleteSound();

    const headline = isRecord ? i18next.t('wagon.newBest') : i18next.t('wagon.gameOver');
    const message = `${headline}\n${i18next.t('wagon.shipped', { n: this.loadsShipped })}`;
    const overlay = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, message, {
      fontFamily: FONT_STACK, fontSize: '60px', color: isRecord ? '#4ecdc4' : '#ffd166',
      padding: { x: 34, y: 20 }, align: 'center',
    }).setOrigin(0.5).setAlpha(0).setScale(0.8).setDepth(100);
    const overlayBg = addRoundedLabelBg(this, overlay, { radius: 24 });
    overlayBg.setAlpha(0).setScale(0.8);

    const cont = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 130, i18next.t('wagon.continue'), {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#dfe6ee', padding: { x: 16, y: 9 },
    }).setOrigin(0.5).setDepth(100);
    addRoundedLabelBg(this, cont);

    this.tweens.add({ targets: [overlay, overlayBg], alpha: 1, scale: 1, duration: 300, ease: 'Back.easeOut' });

    const shownAt = performance.now();
    const leave = (): void => {
      if (performance.now() - shownAt < 900) return;
      this.input.off('pointerdown', leave);
      this.scene.start('Menu');
    };
    this.input.on('pointerdown', leave);

    maybeShowBuyPrompt(this, { delayMs: 1500 });
    maybeAskForRating(this, { kind: 'wagon', loads: this.loadsShipped });
  }
}

/** Resolve a chosen background to its image asset slug (fable has day/night variants). */
function backgroundAsset(bg: Background): string {
  return bg === 'fable' ? fableVariant() : bg;
}
